import * as XLSX from 'xlsx';
import type { AmbulancePricingSlab } from '../models/MasterAdmin/ambulanceBookings';

export type AmbulancePricingExportColumnKey =
  | 'vehicleType'
  | 'fromKm'
  | 'toKm'
  | 'pricingMode'
  | 'WITH_OXYGEN'
  | 'WITHOUT_OXYGEN'
  | 'AC_WITH_OXYGEN'
  | 'AC_WITHOUT_OXYGEN'
  | 'attendantCharge'
  | 'mortuaryCharge'
  | 'facilityChargesJson';

export type AmbulancePricingExportColumn = {
  key: AmbulancePricingExportColumnKey;
  label: string;
  defaultChecked: boolean;
};

export const AMBULANCE_PRICING_EXPORT_COLUMNS: AmbulancePricingExportColumn[] = [
  { key: 'vehicleType', label: 'Vehicle Type', defaultChecked: true },
  { key: 'fromKm', label: 'From KM', defaultChecked: true },
  { key: 'toKm', label: 'To KM', defaultChecked: true },
  { key: 'pricingMode', label: 'Pricing Mode', defaultChecked: true },
  { key: 'WITH_OXYGEN', label: 'WITH_OXYGEN', defaultChecked: true },
  { key: 'WITHOUT_OXYGEN', label: 'WITHOUT_OXYGEN', defaultChecked: true },
  { key: 'AC_WITH_OXYGEN', label: 'AC_WITH_OXYGEN', defaultChecked: true },
  { key: 'AC_WITHOUT_OXYGEN', label: 'AC_WITHOUT_OXYGEN', defaultChecked: true },
  { key: 'attendantCharge', label: 'Attendant Charge', defaultChecked: true },
  { key: 'mortuaryCharge', label: 'Mortuary Charge', defaultChecked: true },
  { key: 'facilityChargesJson', label: 'Facility Charges (JSON)', defaultChecked: true },
];

export type AmbulancePricingExportFilters = {
  vehicleTypes: string[];
  pricingModes: Array<'FLAT' | 'PER_KM'>;
};

const getColumnValue = (
  row: AmbulancePricingSlab,
  key: AmbulancePricingExportColumnKey
): string | number => {
  switch (key) {
    case 'vehicleType':
      return row.vehicleType ?? '';
    case 'fromKm':
      return row.fromKm ?? 0;
    case 'toKm':
      return row.toKm == null ? '' : row.toKm;
    case 'pricingMode':
      return row.pricingMode ?? 'FLAT';
    case 'WITH_OXYGEN':
      return row.WITH_OXYGEN ?? 0;
    case 'WITHOUT_OXYGEN':
      return row.WITHOUT_OXYGEN ?? 0;
    case 'AC_WITH_OXYGEN':
      return row.AC_WITH_OXYGEN ?? 0;
    case 'AC_WITHOUT_OXYGEN':
      return row.AC_WITHOUT_OXYGEN ?? 0;
    case 'attendantCharge':
      return row.attendantCharge ?? 0;
    case 'mortuaryCharge':
      return row.mortuaryCharge ?? 0;
    case 'facilityChargesJson':
      return row.facilityCharges && Object.keys(row.facilityCharges).length
        ? JSON.stringify(row.facilityCharges)
        : '';
    default:
      return '';
  }
};

export const filterAmbulancePricingForExport = (
  rows: AmbulancePricingSlab[],
  filters: AmbulancePricingExportFilters
): AmbulancePricingSlab[] => {
  return rows.filter((row) => {
    const vehicleOk =
      !filters.vehicleTypes.length ||
      filters.vehicleTypes.includes(String(row.vehicleType ?? '').trim());
    const modeOk =
      !filters.pricingModes.length ||
      filters.pricingModes.includes(row.pricingMode ?? 'FLAT');
    return vehicleOk && modeOk;
  });
};

export const sanitizeAmbulancePricingFileName = (agencyName: string, vendorId: string): string => {
  const fromName = agencyName
    .trim()
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

  if (fromName) return fromName;
  return `vendor-${vendorId.slice(0, 8)}`;
};

export const exportAmbulancePricingToExcel = (
  rows: AmbulancePricingSlab[],
  selectedColumns: AmbulancePricingExportColumnKey[],
  options: { vendorId: string; agencyName: string }
): void => {
  const { vendorId, agencyName } = options;
  const columns = AMBULANCE_PRICING_EXPORT_COLUMNS.filter((col) =>
    selectedColumns.includes(col.key)
  );
  if (!columns.length || !rows.length) return;

  const headerRow = columns.map((col) => col.key);
  const dataRows = rows.map((row) =>
    columns.map((col) => getColumnValue(row, col.key))
  );
  const agencyLabel = agencyName.trim() || 'Ambulance Agency';

  const sheet = XLSX.utils.aoa_to_sheet([
    ['Agency Name', agencyLabel],
    ['Vendor ID', vendorId],
    [],
    headerRow,
    ...dataRows,
  ]);
  sheet['!cols'] = columns.map((col) => ({
    wch: col.key === 'facilityChargesJson' ? 40 : Math.max(col.label.length, 14),
  }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Pricing');

  const date = new Date().toISOString().slice(0, 10);
  const fileSlug = sanitizeAmbulancePricingFileName(agencyName, vendorId);
  XLSX.writeFile(workbook, `ambulance-pricing-${fileSlug}-${date}.xlsx`);
};
